import { supabase } from '../lib/supabase';

const TABLE_NAME = 'task_types';

/**
 * Task types drive task creation and workflow:
 * - Task creation form: dropdown options come from active task types.
 * - On create: default_status and (if applicable) default_due_date_offset are applied.
 * - Validation: allowed_statuses can restrict which statuses are valid for a task type.
 */

export const getTaskTypes = async (activeOnly = false) => {
  let query = supabase
    .from(TABLE_NAME)
    .select('*')
    .order('display_order', { ascending: true })
    .order('name', { ascending: true });
  if (activeOnly) {
    query = query.eq('active', true);
  }
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

export const getTaskTypeByCode = async (code) => {
  const { data, error } = await supabase
    .from(TABLE_NAME)
    .select('*')
    .eq('code', code)
    .maybeSingle();
  if (error) throw error;
  return data;
};

const defaultCompletionActions = () => ({ allow_completion_note: true, allow_create_follow_up: false });

export const createTaskType = async (payload) => {
  const actions = payload.completion_actions != null
    ? { ...defaultCompletionActions(), ...payload.completion_actions }
    : defaultCompletionActions();
  const row = {
    name: payload.name,
    code: payload.code || payload.name.toLowerCase().replace(/\s+/g, '_'),
    description: payload.description || null,
    default_status: payload.default_status || 'scheduled',
    allowed_statuses: payload.allowed_statuses || ['scheduled', 'arrived', 'started', 'completed'],
    default_due_date_offset_days: payload.default_due_date_offset_days ?? 0,
    active: payload.active !== false,
    display_order: payload.display_order ?? 0,
    requires_completion_modal: payload.requires_completion_modal === true,
    completion_actions: actions,
    updated_at: new Date().toISOString(),
  };
  const { data, error } = await supabase.from(TABLE_NAME).insert([row]).select().single();
  if (error) throw error;
  return data;
};

export const updateTaskType = async (id, payload) => {
  const updates = {
    updated_at: new Date().toISOString(),
  };
  if (payload.name !== undefined) updates.name = payload.name;
  if (payload.code !== undefined) updates.code = payload.code;
  if (payload.description !== undefined) updates.description = payload.description;
  if (payload.default_status !== undefined) updates.default_status = payload.default_status;
  if (payload.allowed_statuses !== undefined) updates.allowed_statuses = payload.allowed_statuses;
  if (payload.default_due_date_offset_days !== undefined) updates.default_due_date_offset_days = payload.default_due_date_offset_days;
  if (payload.active !== undefined) updates.active = payload.active;
  if (payload.display_order !== undefined) updates.display_order = payload.display_order;
  if (payload.requires_completion_modal !== undefined) updates.requires_completion_modal = payload.requires_completion_modal;
  if (payload.completion_actions !== undefined) updates.completion_actions = payload.completion_actions;
  const { data, error } = await supabase.from(TABLE_NAME).update(updates).eq('id', id).select().single();
  if (error) throw error;
  return data;
};

export const deleteTaskType = async (id) => {
  const { error } = await supabase.from(TABLE_NAME).delete().eq('id', id);
  if (error) throw error;
};
